import React from 'react';
import { HeroSection } from '../components/HeroSection';
import { StatsBar } from '../components/StatsBar';
import { FeatureCard } from '../components/FeatureCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { ROICalculator } from '../components/ROICalculator';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Zap, BarChart3, Search, MousePointerClick, MessageSquare, Shield, ArrowRight } from 'lucide-react';
export function HomePage() {
  return <div className="bg-white" data-id="element-364">
      <HeroSection data-id="element-365" />
      <StatsBar data-id="element-366" />

      {/* Problem Section */}
      <section className="py-24 bg-gray-50" data-id="element-367">
        <Container data-id="element-368">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-369">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-370">
              Your Klaviyo Flows Are Leaving Money on the Table
            </h2>
            <p className="mt-4 text-lg text-gray-600" data-id="element-371">
              Most brands only optimize 10% of their flows. The rest are
              silently leaking revenue.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-372">
            {[{
            icon: Search,
            title: "Underperforming flows you don't know about",
            desc: 'Hidden issues and broken logic costing you revenue every single day.'
          }, {
            icon: MousePointerClick,
            title: 'No clear path to improvement',
            desc: 'Guessing what to fix wastes time. You need data-driven direction.'
          }, {
            icon: BarChart3,
            title: "Can't prove marketing ROI",
            desc: 'Stakeholders demand results, but attributing revenue is complex.'
          }].map((item, i) => <div key={i} className="text-center p-6" data-id="element-373">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-6" data-id="element-374">
                  <item.icon className="h-8 w-8 text-red-600" data-id="element-375" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-376">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-377">{item.desc}</p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Solution Section */}
      <section className="py-24" data-id="element-378">
        <Container data-id="element-379">
          <div className="text-center mb-16" data-id="element-380">
            <span className="text-primary font-semibold tracking-wide uppercase text-sm" data-id="element-381">
              Solution
            </span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-382">
              Meet Flowsense: Your AI Flow Optimization Co-Pilot
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-383">
            {[{
            title: 'Instant Flow Analysis',
            desc: 'AI scans every flow, finds issues in minutes, and ranks them by revenue impact.',
            icon: Zap
          }, {
            title: 'Actionable Recommendations',
            desc: 'Get prioritized fixes with specific copy suggestions you can implement instantly.',
            icon: MessageSquare
          }, {
            title: 'Automatic ROI Tracking',
            desc: 'Track before/after metrics for every change to prove your exact revenue impact.',
            icon: BarChart3
          }].map((item, i) => <FeatureCard key={i} {...item} data-id="element-384" />)}
          </div>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-black text-white" data-id="element-385">
        <Container data-id="element-386">
          <div className="text-center mb-16" data-id="element-387">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl" data-id="element-388">
              From Connection to Optimization in 3 Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-389">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gray-700 -z-10" data-id="element-390"></div>

            {[{
            step: '01',
            title: 'Connect Klaviyo',
            desc: 'One-click OAuth integration. Secure and read-only access.'
          }, {
            step: '02',
            title: 'Get AI Insights',
            desc: 'Automatic flow analysis & prioritized recommendations.'
          }, {
            step: '03',
            title: 'Track Results',
            desc: 'Real-time ROI reporting & performance tracking.'
          }].map((item, i) => <div key={i} className="relative text-center" data-id="element-391">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gray-800 border-4 border-black text-3xl font-bold text-white mb-6" data-id="element-392">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-393">
                  {item.title}
                </h3>
                <p className="text-gray-400" data-id="element-394">{item.desc}</p>
              </div>)}
          </div>
          <div className="mt-16 text-center" data-id="element-395">
            <a href="https://app.useflowsense.com/auth/signup" data-id="element-396">
              <Button size="lg" className="px-12" data-id="element-397">
                Signup Free
              </Button>
            </a>
          </div>
        </Container>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-gray-50" data-id="element-398">
        <Container data-id="element-399">
          <div className="text-center mb-16" data-id="element-400">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-401">
              Everything You Need to Optimize Flows
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-402">
            {[{
            icon: Zap,
            title: 'AI-Powered Analysis',
            desc: 'Deep learning models trained on millions of emails identify opportunities humans miss.'
          }, {
            icon: BarChart3,
            title: 'Performance Tracking',
            desc: 'Automatic before/after metrics for every change you make.'
          }, {
            icon: MessageSquare,
            title: 'Copy Suggestions',
            desc: 'AI-generated subject lines, CTAs, and body copy optimized for conversion.'
          }, {
            icon: Search,
            title: 'Brand Voice Analysis',
            desc: "Ensures all recommendations match your brand's unique tone and style."
          }, {
            icon: MousePointerClick,
            title: 'Agency Tools',
            desc: 'Multi-client management & white-label reports for agencies.'
          }, {
            icon: Shield,
            title: 'Enterprise Security',
            desc: 'SOC 2 compliant, GDPR ready, and bank-grade encryption.'
          }].map((item, i) => <FeatureCard key={i} {...item} data-id="element-403" />)}
          </div>
        </Container>
      </section>

      {/* ROI Calculator */}
      <section className="py-24" data-id="element-404">
        <Container data-id="element-405">
          <ROICalculator data-id="element-406" />
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50" data-id="element-407">
        <Container data-id="element-408">
          <div className="text-center mb-16" data-id="element-409">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl" data-id="element-410">
              Loved by Brands & Agencies
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-411">
            {[{
            quote: 'Increased cart abandonment revenue by 43% in just two weeks. The AI suggestions were spot on.',
            author: 'Sarah Jenkins',
            role: 'CMO',
            company: 'StyleBrand',
            image: 'https://placehold.co/100x100?text=SJ'
          }, {
            quote: "Saves us 10 hours per client per month. It's like having a senior retention specialist on autopilot.",
            author: 'Mike Chen',
            role: 'Founder',
            company: 'GrowthAgency',
            image: 'https://placehold.co/100x100?text=MC'
          }, {
            quote: 'Finally, we can prove email marketing ROI to our board. The before/after tracking is a game changer.',
            author: 'Elena Rodriguez',
            role: 'VP Marketing',
            company: 'TechFlow',
            image: 'https://placehold.co/100x100?text=ER'
          }].map((item, i) => <TestimonialCard key={i} {...item} data-id="element-412" />)}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white" data-id="element-413">
        <Container data-id="element-414">
          <div className="bg-gray-900 rounded-3xl overflow-hidden shadow-2xl text-center py-16 px-6 lg:py-24 lg:px-12 relative" data-id="element-415">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10" data-id="element-416">
              <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-417">
                <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-418" />
              </svg>
            </div>

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-419">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6" data-id="element-420">
                Ready to Optimize Your Flows?
              </h2>
              <p className="text-xl text-gray-300 mb-10" data-id="element-421">
                Join 500+ brands improving their Klaviyo performance today.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-422">
                <a href="https://app.useflowsense.com/auth/signup" data-id="element-423">
                  <Button size="lg" rightIcon={<ArrowRight className="h-5 w-5" data-id="element-425" />} data-id="element-424">
                    Signup Free
                  </Button>
                </a>
                <Button variant="outline" size="lg" className="bg-white text-gray-900 border-white hover:bg-gray-100" data-id="element-426">
                  Schedule Demo
                </Button>
              </div>
              <p className="mt-6 text-sm text-gray-400" data-id="element-427">
                No credit card required • Try for free • Cancel anytime
              </p>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}